import { useFormDataContext } from "@totalenergiescode/mobility-business-rev-form-data/components";
import type { SubmittedData } from "@totalenergiescode/mobility-business-rev-form-data/lib";
import { useState } from "react";

import {
  StyledNavigationButtonsContainer,
  StyledNextButton,
  StyledPreviousButton,
} from "@/components/FormData/lib/layout/Renderers/styles";
import { useAddSnackbar } from "@/contexts/snackbar/context";

export interface NavigationRendererProps {
  onSubmitted?: (data: SubmittedData) => Promise<void>;
}

const NavigationRenderer = ({ onSubmitted }: NavigationRendererProps) => {
  const { form, translator } = useFormDataContext();
  const addSnackbar = useAddSnackbar();
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = () => {
    if (onSubmitted) {
      setSubmitted(true);
      onSubmitted(form.submit()).catch(() => setSubmitted(false));
    }
  };

  function handleNextStep() {
    const has_moved = form.next();

    if (!has_moved) {
      addSnackbar({
        type: "error",
        title: translator.translate("common.error.defaultTitle"),
        message: translator.translate("common.error.default"),
        isAutoClose: true,
      });
    }
  }

  const isPreviousButtonDisabled = form.canDisplaySummary()
    ? submitted || !form.hasPrevious()
    : !form.hasPrevious();

  const isNextButtonDisabled = form.canDisplaySummary()
    ? submitted && !form.hasNext()
    : !form.hasNext();

  return (
    <StyledNavigationButtonsContainer>
      <StyledPreviousButton
        variant='secondary'
        onClick={() => form.previous()}
        disabled={isPreviousButtonDisabled}
      >
        {translator.translate("formData.buttons.previous")}
      </StyledPreviousButton>
      <StyledNextButton
        variant='primary'
        onClick={() =>
          form.canDisplaySummary() ? handleSubmit() : handleNextStep()
        }
        disabled={isNextButtonDisabled}
      >
        {form.canDisplaySummary()
          ? translator.translate("formData.buttons.submit")
          : translator.translate("formData.buttons.next")}
      </StyledNextButton>
    </StyledNavigationButtonsContainer>
  );
};

export default NavigationRenderer;
